/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React from 'react';
import { Star, Quote, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';

const ReviewCard = ({ name, role, text, location, delay }: any) => (
  <motion.div 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay }}
    className="bg-white dark:bg-[#15171C] p-10 rounded-card border border-stone-100 dark:border-white/5 relative group hover:shadow-2xl transition-all duration-500 overflow-hidden"
  >
      {/* Ghostly Glow Effect */}
      <div className="absolute -top-10 -right-10 w-32 h-32 bg-nobel-gold/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity" />
      
      <Quote className="absolute top-8 right-8 text-stone-100 dark:text-white/5 group-hover:text-nobel-gold/10 transition-colors" size={48} />
      
      <div className="flex gap-1.5 text-nobel-gold mb-8">
          {[...Array(5)].map((_, i) => <Star key={i} size={14} fill="currentColor" />)}
      </div>
      
      <p className="text-stone-600 dark:text-stone-300 mb-10 leading-relaxed font-light text-lg italic">
        "{text}"
      </p>
      
      <div className="flex items-center gap-5 pt-8 border-t border-stone-50 dark:border-white/5">
          <div className="w-12 h-12 rounded-xl bg-[#0A0B0F] dark:bg-white flex items-center justify-center font-serif font-bold text-white dark:text-[#0A0B0F] border border-white/10 shadow-lg group-hover:scale-110 transition-transform duration-500">
              {name.charAt(0)}
          </div>
          <div>
              <div className="font-bold text-[#0A0B0F] dark:text-white text-base flex items-center gap-2">
                {name} <ShieldCheck size={14} className="text-emerald-500" />
              </div>
              <div className="text-[10px] text-stone-400 uppercase tracking-widest font-bold">{role} • {location}</div>
          </div>
      </div>
  </motion.div>
);

export const Testimonials: React.FC = () => {
  return (
    <section className="py-32 bg-white dark:bg-[#0A0B0F] relative overflow-hidden">
      <div className="container mx-auto px-6 relative z-10">
         <div className="text-center mb-24">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-stone-100 dark:bg-white/5 text-[10px] font-bold uppercase tracking-widest text-stone-500 mb-6">
                Verified Global Merchant Network
            </div>
            <h2 className="font-serif text-4xl md:text-6xl text-[#0A0B0F] dark:text-white mb-6 font-bold tracking-tight">
                Trusted by the <span className="italic text-stone-400">Sovereign.</span>
            </h2>
            <p className="text-stone-500 dark:text-stone-400 text-lg max-w-2xl mx-auto">
                Join thousands of businesses and high-net-worth individuals reclaiming their financial privacy with GhostOS.
            </p>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-8">
            <ReviewCard 
                delay={0.1}
                name="Elena R."
                role="Art Dealer"
                location="Zurich"
                text="GhostOS allows me to accept high-value payments from international clients instantly, without the invasive surveillance of traditional banks. It's simply superior technology."
            />
            <ReviewCard 
                delay={0.2}
                name="Marcus K."
                role="Architect"
                location="Singapore"
                text="The escrow functionality is a game changer for my contracting work. I don't write a single line of code until the funds are verified on-chain. No more chasing invoices."
            />
            <ReviewCard 
                delay={0.3}
                name="Sarah J."
                role="Merchant"
                location="Austin"
                text="Setting up the POS took literally 5 minutes. The fees are non-existent compared to Stripe, and settlement is instant. I've completely switched over to the Ghost stack."
            />
         </div>
      </div>
    </section>
  );
};